// src/interfaces/Public/PublicHome.jsx
// Accueil PUBLIC — riche, animé et imagé, optimisé SEO ("pièces auto B2B Tunisie").
import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import http from "../../api/http";
import Seo, { SITE_URL } from "../../component/Seo/Seo";
import Reveal from "../../component/ui/Reveal";
import CountUp from "../../component/ui/CountUp";
import { FiSearch, FiCheckCircle, FiTruck, FiTag, FiArrowRight, FiEye } from "react-icons/fi";
import { FaBoxes, FaWarehouse, FaLink, FaUsers, FaBalanceScale, FaShieldAlt, FaWhatsapp } from "react-icons/fa";
import "./publicHome.css";

import carBg from "../../images/bg-car.png";
import amenMark from "../../images/amen.png";
import mgMark from "../../images/MG.png";
import imgFreinage from "../../images/Freinage.png";
import imgFiltre from "../../images/Filtre.png";
import imgSuspens from "../../images/suspens.png";
import imgAmort from "../../images/Amortissement.png";
import imgCourroie from "../../images/Commande-courroie.png";
import imgRefroid from "../../images/Refroidissement.png";
import imgCarros from "../../images/Carrosserie.png";
import imgEssuie from "../../images/essuie-glaces.png";

const CATEGORIES = [
  { t: "Freinage", img: imgFreinage, d: "Plaquettes, disques, étriers et kits de frein." },
  { t: "Filtration", img: imgFiltre, d: "Filtres à huile, à air, à carburant et d'habitacle." },
  { t: "Suspension & Direction", img: imgSuspens, d: "Amortisseurs, rotules, biellettes, silentblocs." },
  { t: "Amortissement", img: imgAmort, d: "Amortisseurs et kits de suspension complets." },
  { t: "Distribution", img: imgCourroie, d: "Courroies, kits de distribution et pompes." },
  { t: "Refroidissement", img: imgRefroid, d: "Radiateurs, thermostats et pompes à eau." },
  { t: "Carrosserie", img: imgCarros, d: "Optiques, pare-chocs et pièces de carrosserie." },
  { t: "Essuie-glaces", img: imgEssuie, d: "Balais et mécanismes d'essuie-glaces." },
];

const BRANDS = ["BOSCH", "VALEO", "SKF", "FAG", "LUK", "SACHS", "GMB", "NSK", "EXEDY", "MANN-FILTER", "TRW", "FEBI", "NGK", "DENSO"];

const STATS = [
  { icon: <FaBoxes />, end: 50000, suffix: "+", label: "Références en catalogue" },
  { icon: <FaWarehouse />, end: 200000, suffix: "+", label: "Pièces en stock" },
  { icon: <FaLink />, end: 120000, suffix: "+", label: "Correspondances OE" },
  { icon: <FaUsers />, end: 1200, suffix: "+", label: "Revendeurs partenaires" },
];

const WHY = [
  { icon: <FaBalanceScale />, t: "Notre philosophie", d: "L'éthique guide notre savoir-faire : qualité de service, efficacité et professionnalisme dans chaque intervention." },
  { icon: <FiEye />, t: "Notre vision", d: "N°1 en Tunisie des pièces d'origine, avec l'ambition de devenir le distributeur leader en Afrique." },
  { icon: <FaUsers />, t: "Personnel qualifié", d: "Des équipes commerciales et techniques expérimentées, formées par nos partenaires équipementiers et toujours disponibles." },
  { icon: <FaShieldAlt />, t: "Perfection & garantie", d: "Des produits contrôlés et garantis selon les normes internationales, au service de votre satisfaction." },
];

export default function PublicHome() {
  // Logos de marques récupérés depuis l'API (/api/partenaires -> images)
  const [logos, setLogos] = useState([]);
  useEffect(() => {
    // Pas de logos dans le HTML pré-rendu par react-snap : ce sont des images base64
    // (jusqu'à 4,5 Mo l'une) qui gonflaient index.html à ~10 Mo. Chargés côté navigateur.
    if (navigator.userAgent === "ReactSnap") return;
    let alive = true;
    http.get("/api/partenaires")
      .then(({ data }) => {
        const list = (Array.isArray(data) ? data : [])
          .map((x) => String(x?.urlimg || "").trim())
          .filter(Boolean);
        if (alive) setLogos(list);
      })
      .catch(() => { if (alive) setLogos([]); });
    return () => { alive = false; };
  }, []);

  const heroRef = useRef(null);
  const onMove = (e) => {
    const el = heroRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
    el.style.setProperty("--my", ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
  };
  const onLeave = () => { const el = heroRef.current; if (el) { el.style.setProperty("--mx", "0"); el.style.setProperty("--my", "0"); } };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AutoPartsStore",
    name: "Amen Distribution & MG",
    alternateName: ["Amen Distribution", "MG Distribution"],
    description: "Grossiste B2B de pièces auto d'origine et adaptables en Tunisie — les réseaux Amen Distribution et MG.",
    url: SITE_URL, logo: SITE_URL + "/logo512.png", image: SITE_URL + "/logo512.png",
    priceRange: "$$", areaServed: { "@type": "Country", name: "Tunisie" },
    address: { "@type": "PostalAddress", addressCountry: "TN", addressLocality: "Tunis" },
    sameAs: ["https://amenedistribution.com"],
  };

  return (
    <>
      <Seo
        title="Pièces auto & pièces de rechange B2B en Tunisie"
        description="Amen Distribution & MG : le plus grand catalogue B2B de pièces auto d'origine et adaptables en Tunisie. Freinage, embrayage, filtration, roulements — recherche par VIN, livraison 24h."
        path="/"
        jsonLd={jsonLd}
      />

      {/* ---------- HERO ---------- */}
      <section className="ph-hero" ref={heroRef} onMouseMove={onMove} onMouseLeave={onLeave}>
        <div className="ph-hero__bg" style={{ backgroundImage: `url(${carBg})` }} aria-hidden="true" />
        <div className="ph-hero__overlay" aria-hidden="true" />
        <div className="ph-aurora" aria-hidden="true"><span className="ph-blob b1" /><span className="ph-blob b2" /></div>

        <div className="ph-hero__inner">
          <span className="ph-eyebrow"><span className="ph-dot" /> Amen Distribution &amp; MG · Grossiste B2B · Tunisie</span>
          <h1 className="ph-h1">
            Pièces auto & pièces de rechange <span className="ph-accent">B2B en Tunisie</span>
          </h1>
          <p className="ph-lead">
            Amen Distribution & MG — le plus grand catalogue de <strong>pièces auto d'origine
            et adaptables</strong> de Tunisie. Recherche par VIN, prix revendeur et <strong>livraison 24 h</strong>.
          </p>
          <div className="ph-actions">
            <Link to="/login" className="ph-btn ph-btn--primary"><FiSearch /> Accéder à l'espace client</Link>
            <Link to="/login" className="ph-btn ph-btn--ghost">Demander un accès B2B <FiArrowRight /></Link>
          </div>
          <ul className="ph-chips">
            <li><FiCheckCircle /> Origine & adaptable</li>
            <li><FiTruck /> Livraison 24h</li>
            <li><FiTag /> Prix revendeur</li>
          </ul>

          <div className="ph-cobrand">
            <span className="ph-cobrand__label">Deux réseaux, un seul partenaire</span>
            <div className="ph-cobrand__logos">
              <span className="ph-cobrand__chip"><img src={amenMark} alt="Amen Distribution" /></span>
              <span className="ph-cobrand__chip"><img src={mgMark} alt="MG Distribution" /></span>
            </div>
          </div>
        </div>
        <div className="ph-scroll" aria-hidden="true"><span /></div>
      </section>

      {/* ---------- STATS (compteurs animés) ---------- */}
      <section className="ph-stats" aria-label="Chiffres clés">
        <div className="ph-stats__inner">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 90} className="ph-stat">
              <div className="ph-stat__ic">{s.icon}</div>
              <div className="ph-stat__num"><CountUp end={s.end} suffix={s.suffix} /></div>
              <div className="ph-stat__lbl">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- CATÉGORIES (images) ---------- */}
      <section className="ph-section" id="categories">
        <Reveal><h2 className="ph-h2">Nos catégories de pièces détachées</h2></Reveal>
        <Reveal delay={60}><p className="ph-sub">Un catalogue complet de pièces de rechange automobiles pour toutes les marques, en pièces d'origine comme en adaptables de qualité.</p></Reveal>
        <div className="ph-cats">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.t} delay={(i % 4) * 80} className="ph-cat">
              <Link to="/login" className="ph-cat__link" aria-label={`${c.t} — voir dans l'espace client`}>
                <div className="ph-cat__img"><img src={c.img} alt={c.t} loading="lazy" /></div>
                <div className="ph-cat__body">
                  <h3>{c.t}</h3>
                  <p>{c.d}</p>
                  <span className="ph-cat__cta">Voir <FiArrowRight /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- MARQUES (grille éditoriale de logos depuis l'API) ---------- */}
      <section className="ph-brands-sec" id="marques">
        <div className="ph-brands-head">
          <Reveal><span className="ph-brands-eyebrow">Marques distribuées</span></Reveal>
          <Reveal delay={60}><h2 className="ph-brands-title">Les grandes marques, d'origine comme adaptables.</h2></Reveal>
          <Reveal delay={120}><p className="ph-brands-note">Une liste précise, limitée, assumée — les équipementiers qui comptent réellement en atelier.</p></Reveal>
        </div>

        <Reveal>
          {logos.length > 0 ? (
            <div className="ph-bands">
              {/* Bande 1 : défile de droite à gauche */}
              <div className="ph-band">
                <div className="ph-band__track ph-band__track--left">
                  {[...logos.slice(0, 12), ...logos.slice(0, 12)].map((u, i) => (
                    <div className="ph-logo" key={i} aria-hidden={i >= 12 ? "true" : undefined}>
                      <img src={u} alt="Marque de pièces auto distribuée par Amen Distribution" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
              {/* Bande 2 : défile de gauche à droite */}
              <div className="ph-band">
                <div className="ph-band__track ph-band__track--right">
                  {[...logos.slice(12, 24), ...logos.slice(12, 24)].map((u, i) => (
                    <div className="ph-logo" key={i} aria-hidden={i >= 12 ? "true" : undefined}>
                      <img src={u} alt="Marque de pièces auto distribuée par Amen Distribution" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Repli : noms de marques si les logos ne sont pas chargés */
            <div className="ph-logos ph-logos--text">
              {BRANDS.map((b) => <span className="ph-brand-pill" key={b}>{b}</span>)}
            </div>
          )}
        </Reveal>
      </section>

      {/* ---------- POURQUOI NOUS ---------- */}
      <section className="ph-section" id="pourquoi">
        <Reveal><h2 className="ph-h2">Pourquoi choisir Amen Distribution ?</h2></Reveal>
        <div className="ph-why">
          {WHY.map((w, i) => (
            <Reveal key={w.t} delay={i * 130} className="ph-why__item">
              <div className="ph-why__ic">{w.icon}</div>
              <h3>{w.t}</h3>
              <p>{w.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- CTA final ---------- */}
      <section className="ph-cta" id="contact" style={{ backgroundImage: `url(${carBg})` }}>
        <div className="ph-cta__overlay" aria-hidden="true" />
        <Reveal className="ph-cta__inner">
          <h2>Amen Distribution &amp; MG, votre grossiste pièces auto en Tunisie</h2>
          <p>
            Nous fournissons les <strong>ateliers, garages et revendeurs</strong> en pièces détachées
            pour toutes les marques — véhicules particuliers et utilitaires. Plus de 50 000 références,
            recherche par VIN, disponibilité en temps réel et livraison 24 h. Rejoignez les
            1 200+ professionnels qui nous font confiance.
          </p>
          <Link to="/login" className="ph-btn ph-btn--primary ph-btn--lg"><FiSearch /> Créer / accéder à mon compte revendeur</Link>
        </Reveal>
      </section>

      {/* ---------- Bulle WhatsApp flottante ---------- */}
      <a
        className="ph-wa"
        href="https://wa.me/21655310215"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Nous contacter sur WhatsApp"
      >
        <FaWhatsapp />
        <span className="ph-wa__tip">Besoin d'aide ?</span>
      </a>
    </>
  );
}
